import * as React from 'react';
import { styled } from '@linaria/react';

import { range } from '@/utils';
import useWindowDimensions from '@/hooks/use-window-dimensions';

const Ticker = React.memo(function Ticker({
  totalNumOfFrames,
  isMobile,
}: {
  totalNumOfFrames: number;
  isMobile: boolean;
}) {
  return (
    <Wrapper>
      {range(totalNumOfFrames - 1).map((index: number) => {
        const showEvery = isMobile ? 2 : 1;
        const emphasizeEvery = isMobile ? 20 : 10;

        const isShown = index === 0 || (index + 1) % showEvery === 0;
        const isEmphasized =
          index === 0 || (index + 1) % emphasizeEvery === 0;

        const Tick = isEmphasized ? BigTick : SmallTick;

        return (
          <Column key={index}>
            <Tick style={{ opacity: isShown ? 1 : 0 }}>
              {isEmphasized && <Num>{index + 1}</Num>}
            </Tick>
            {index === totalNumOfFrames - 2 && <FinalTick />}
          </Column>
        );
      })}
    </Wrapper>
  );
});

// This component renders a different # of ticks on desktop/mobile. This means that the component will always re-render on mount, since `windowWidth` goes from `undefined` to a number.
// To prevent this re-render on mobile devices, I'm memoizing the underlying component, and passing `isMobile`, which will remain true on mobile (and go from `false` to `true` on desktop).
// This also prevents the component from re-rendering when the window is resized.
function TickerWrapper({
  totalNumOfFrames,
}: {
  totalNumOfFrames: number;
}) {
  const { width: windowWidth } = useWindowDimensions();
  // We control how dense the ticks are depending on screen size. During SSR, assume that we’re on mobile.
  // TODO: This could be a lot better. We could figure out the min number of pixels per tick, and have a `condensed` prop that adjusts for each one. But whatever, it works well enough as-is.
  const isMobile =
    typeof windowWidth === 'number' ? windowWidth < 500 : true;

  return (
    <Ticker totalNumOfFrames={totalNumOfFrames} isMobile={isMobile} />
  );
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  height: 2rem;
  margin-top: 6px;
  margin-bottom: -8px;
  cursor: pointer;
  user-select: none;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -1px;
    right: -1px;
    height: 2px;
    border-radius: 2px 2px 2px 0px;
    background: white;
  }
`;

const Column = styled.div`
  position: relative;
  flex: 1;
`;

const BaseTick = styled.div`
  position: relative;
  transform: translateX(-50%);
`;

const SmallTick = styled(BaseTick)`
  width: 1px;
  height: 4px;
  background: var(--color-gray-300);
  border-radius: 0px 0px 1px 1px;
`;

const BigTick = styled(BaseTick)`
  width: 2px;
  height: 8px;
  background: white;
  border-radius: 0px 0px 2px 2px;
`;

const FinalTick = styled(SmallTick)`
  position: absolute;
  top: 0;
  right: -0.5px;
  transform: revert;
`;

const Num = styled.span`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: white;
`;

export default TickerWrapper;
